import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, FileText, FileType, Star, Printer, Download, Upload,
  Pencil, Trash2, Plus, ChevronDown, X, Wrench, Home, Salad, HeartPulse,
  Briefcase, Building2, Siren, AlertTriangle, ClipboardList,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '../api/client.ts';
import { useAuth } from '../contexts/AuthContext';
import { useFacility, useFacilityLogoUrl } from '../contexts/FacilityContext.tsx';
import PageFocusToggle from '../components/PageFocusToggle.tsx';
import DarkSelect from '../components/DarkSelect.tsx';
import FormSchemaBuilder, { type FormField, splitFieldsIntoSections, mergeSectionsIntoFields } from '../components/FormSchemaBuilder.tsx';
import { type LogbookFormSchema, type LogbookFormField } from '../components/LogbookFormRenderer.tsx';
import Toast, { useToast } from '../components/Toast';
import '../styles/buttonAnimations.css';

// ─── Types ──────────────────────────────────────────────────────────────────

type Frequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual' | 'as_needed';
type FileKind = 'pdf' | 'docx';
type Source = 'corporate' | 'building';

interface LogbookTemplate {
  id: number;
  facility_id: number;
  name: string;
  department_id: number | null;
  frequency: Frequency;
  file_type: FileKind;
  source: Source;
  required: boolean;
  display_order: number;
  file_url: string | null;
  file_name: string | null;
  form_schema: LogbookFormSchema | null;
  created_at: string;
  updated_at: string;
  department_name: string | null;
  department_color: string | null;
  department_icon: string | null;
}

interface Department {
  id: number;
  name: string;
  color: string;
  icon: string;
  sort_order: number;
}

// ─── Department icon mapping (departments.icon is a plain string — 'tool',
// 'home', 'salad', 'heart-pulse', 'briefcase' per migration_v104.sql seed data) ─
const DEPT_ICON_MAP: Record<string, LucideIcon> = {
  tool: Wrench, home: Home, salad: Salad, 'heart-pulse': HeartPulse, briefcase: Briefcase,
};
function DeptIcon({ icon, size = 14, color }: { icon: string | null; size?: number; color?: string }) {
  const Cmp = (icon && DEPT_ICON_MAP[icon]) || Building2;
  return <Cmp size={size} color={color} />;
}

const FREQ_LABELS: Record<Frequency, string> = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', annual: 'Annual', as_needed: 'As Needed',
};
// Exact colors from mockups/logbook-templates-page-enhanced.html .freq.* classes
const FREQ_COLORS: Record<Frequency, { bg: string; color: string }> = {
  daily: { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa' },
  weekly: { bg: 'rgba(20,184,166,0.15)', color: '#2dd4bf' },
  monthly: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
  quarterly: { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa' },
  annual: { bg: 'rgba(236,72,153,0.15)', color: '#f472b6' },
  as_needed: { bg: 'rgba(100,116,139,0.2)', color: '#94a3b8' },
};
function FreqBadge({ freq }: { freq: Frequency }) {
  const c = FREQ_COLORS[freq];
  return (
    <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 9px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap', background: c.bg, color: c.color }}>
      {FREQ_LABELS[freq]}
    </span>
  );
}
// Exact colors from mockup's .ftype.pdf / .ftype.docx
function FileTypeBadge({ type }: { type: FileKind }) {
  const c = type === 'pdf' ? { bg: 'rgba(239,68,68,0.16)', color: '#f87171' } : { bg: 'rgba(59,130,246,0.16)', color: '#60a5fa' };
  return <span style={{ fontSize: '0.56rem', fontWeight: 800, padding: '2px 7px', borderRadius: 5, letterSpacing: '0.03em', background: c.bg, color: c.color }}>{type.toUpperCase()}</span>;
}
// Exact colors from mockup's .reqd
function RequiredBadge() {
  return <span style={{ fontSize: '0.56rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'rgba(245,158,11,0.14)', color: '#fbbf24', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Star size={9} fill="#fbbf24" /> Required</span>;
}
// Exact colors from mockup's .src.corp / .src.bldg
function SourceBadge({ source }: { source: Source }) {
  return source === 'corporate'
    ? <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: 'rgba(139,92,246,0.14)', color: '#a78bfa' }}>◆ Corporate</span>
    : <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: 'rgba(255,255,255,0.07)', color: 'var(--text-secondary)' }}>🏢 Building</span>;
}
// Same visual treatment as the mockup's .woform badge, repurposed for Phase 2: shown when a
// template has a fillable form_schema attached (work orders pick a template explicitly in their
// own modal — a template doesn't know which WOs used it, so this just means "has a form").
function FormBadge() {
  return <span style={{ fontSize: '0.56rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'rgba(16,185,129,0.14)', color: '#34d399', display: 'inline-flex', alignItems: 'center', gap: 4 }}><ClipboardList size={11} /> Has form</span>;
}

function fileIcon(type: FileKind, size = 18, color?: string) {
  return type === 'pdf' ? <FileText size={size} color={color ?? '#f87171'} /> : <FileType size={size} color={color ?? '#60a5fa'} />;
}

// "Print blank" for a template with no uploaded reference file — file_url-based templates just
// open that file; templates authored purely via the form builder (no file at all) had no print
// path whatsoever ("Print blank"/"Download" were both hard-gated on file_url, scaffolding that
// was never extended once form-only templates became a real, common case). This renders the
// schema's fields as blank fill-in lines/boxes instead.
function schemaHasFillableFields(schema: LogbookFormSchema | null | undefined): boolean {
  if (!schema) return false;
  return schema.form_mode === 'single_form'
    ? (schema.fixed_fields?.length ?? 0) > 0
    : (schema.rows?.length ?? 0) > 0 && (schema.row_fields?.length ?? 0) > 0;
}

// Isolated print window — same established pattern as Tasks.tsx's Compliance PM export
// (handleExportPDF) and Reports.tsx's generateQapiPDF: build a full standalone HTML document,
// open it in a new tab via window.open, write it, then call .print() after a short delay. This
// gives the user an actual on-screen preview of exactly what will print (the new tab renders
// visibly before the print dialog appears) instead of the previous hidden-print-area/CSS
// visibility trick, which never showed anything on screen — the print dialog's own preview pane
// was the only preview a user got.
// Shared CSS for every consumer of the blank-template render (print window, Preview panel
// iframe, and the PDF download capture target) -- one definition, so all three always look
// identical instead of drifting apart. Deliberately all literal hex colors, never var(...) --
// this document must render identically regardless of the live app's current theme (see
// downloadTemplateBlankPdf's LOCKED comment below for why a leaked var() broke this before).
// #0ea5e9 is global.css's --accent-primary, hardcoded here for the same reason.
const TEMPLATE_PRINT_CSS = `
  * { box-sizing: border-box; }
  @page { margin: 14mm 12mm; }
  body { font-family: 'Inter','Helvetica Neue',Arial,sans-serif; color: #1a2233; background: #fff; margin: 0; padding: 0; }
  .sheet { padding: 34px 40px 28px; background: #fff; }

  .header-band { display: flex; align-items: center; gap: 16px; padding-bottom: 14px; margin-bottom: 14px; border-bottom: 2px solid #0ea5e9; }
  .header-logo { max-height: 52px; max-width: 340px; object-fit: contain; flex-shrink: 0; }
  .header-text { flex: 1; min-width: 0; }
  .header-facility { font-size: 0.72rem; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.06em; margin: 0 0 3px; }
  .header-title { font-size: 1.3rem; font-weight: 800; color: #1a2233; margin: 0; }

  .meta-row { display: flex; flex-wrap: wrap; gap: 28px; margin: 0 0 14px; font-size: 0.78rem; color: #444; }
  .meta-item { display: flex; align-items: baseline; gap: 6px; }
  .meta-item .meta-fill { min-width: 130px; border-bottom: 1px solid #999; display: inline-block; }

  .instructions { color: #555; margin: 0 0 18px; white-space: pre-wrap; font-size: 0.85rem; }

  .section-card { border: 1px solid #ddd; border-radius: 8px; padding: 10px 14px; margin-bottom: 6px; page-break-inside: avoid; }
  .section-header { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #0ea5e9; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid rgba(14,165,233,0.35); }
  .field-group { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 14px; }
  .field-group:last-child { margin-bottom: 0; }
  .field-block { flex: 1 1 150px; min-width: 130px; }
  .field-block-wide { flex: 1 1 100%; }
  .field-label { font-size: 0.66rem; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 3px; }
  .field-value { font-size: 0.85rem; border-bottom: 1px solid #ccc; padding-bottom: 3px; min-height: 16px; word-break: break-word; }

  .option-grid { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 3px; }
  .option-box { border: 1px solid #999; border-radius: 5px; padding: 5px 10px; font-size: 0.75rem; min-width: 30px; text-align: center; }

  table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  th, td { padding: 8px 10px; border: 1px solid #ccc; vertical-align: top; color: #1a2233; }
  td.label { font-weight: 600; width: 40%; }
  .meta { font-size: 0.72rem; color: #666; font-weight: 400; margin-top: 2px; }

  .doc-footer { margin-top: 16px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 0.68rem; color: #888; text-align: center; }
`;

// A select field whose options are all bare numbers (e.g. Daily Water Temperatures' room
// pickers: "101","102",...) reads as a room-number list -- distinct from a short select like
// Pass/Fail or Yes/No, which still renders as the plain inline list below. Options-shape-based,
// not label-based, so it applies to any template's room-number select, not just this one.
function isRoomNumberSelect(f: LogbookFormField): boolean {
  return f.type === 'select' && (f.options?.length ?? 0) > 0 && f.options!.every((o) => /^\d+$/.test(o.trim()));
}

// One field's blank fill-in block (label + a line to write/check the value on, or -- for a
// room-number select -- a wrapping grid of small boxes sized to circle by hand when printed,
// instead of a cramped slash-separated inline list). Non-room-number value rendering (checkbox /
// other select-options-joined / blank line) is unchanged from before this redesign.
function fieldBlockHtml(f: LogbookFormField, esc: (s: string) => string): string {
  if (isRoomNumberSelect(f)) {
    return `<div class="field-block field-block-wide">
      <div class="field-label">${esc(f.label)}${f.required ? ' *' : ''}</div>
      <div class="option-grid">${(f.options ?? []).map((o) => `<span class="option-box">${esc(o)}</span>`).join('')}</div>
    </div>`;
  }
  const value = f.type === 'checkbox' ? '&#9744;' : f.type === 'select' ? esc((f.options ?? []).join(' / ')) : '&nbsp;';
  return `<div class="field-block">
    <div class="field-label">${esc(f.label)}${f.required ? ' *' : ''}</div>
    <div class="field-value">${value}</div>
  </div>`;
}

// single_form mode: group fixed_fields into contiguous runs of the same section_id, preserving
// fixed_fields' own order -- the exact same algorithm LogbookFormRenderer.tsx's SingleFormBody
// already uses for the live fill view (a run with a section_id becomes a labeled card; a run
// without renders as a bare unlabeled group), reused here rather than invented fresh so the
// printed/downloaded output visually matches what filling out the form actually looks like. No
// sections defined at all -> one flat unlabeled group, unchanged from before this redesign.
function buildSingleFormHtml(schema: LogbookFormSchema, esc: (s: string) => string): string {
  const fields = schema.fixed_fields ?? [];
  const sections = schema.sections ?? [];
  if (sections.length === 0) {
    return `<div class="field-group">${fields.map(f => fieldBlockHtml(f, esc)).join('')}</div>`;
  }
  const labelBySection = new Map(sections.map(s => [s.section_id, s.label]));
  const segments: { sectionId?: string; fields: LogbookFormField[] }[] = [];
  for (const f of fields) {
    const last = segments[segments.length - 1];
    if (last && last.sectionId === f.section_id) last.fields.push(f);
    else segments.push({ sectionId: f.section_id, fields: [f] });
  }
  return segments.map((seg) => seg.sectionId
    ? `<div class="section-card">
        <div class="section-header">${esc(labelBySection.get(seg.sectionId) ?? 'Section')}</div>
        <div class="field-group">${seg.fields.map(f => fieldBlockHtml(f, esc)).join('')}</div>
      </div>`
    : `<div class="field-group">${seg.fields.map(f => fieldBlockHtml(f, esc)).join('')}</div>`
  ).join('');
}

// The "sheet" body fragment -- header band/instructions/fields/footer -- shared by the full
// standalone print document, the Preview panel iframe, and the PDF capture target below.
function buildTemplateBlankBodyHtml(name: string, schema: LogbookFormSchema, logoUrl?: string | null, facilityName?: string | null): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const bodyContent = schema.form_mode === 'single_form'
    ? buildSingleFormHtml(schema, esc)
    : `<table>
        <thead>
          <tr>
            <th style="text-align:left">Location</th>
            ${(schema.row_fields ?? []).map(f => `<th style="text-align:left">${esc(f.label)}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${(schema.rows ?? []).map(row => `
          <tr>
            <td class="label">
              ${esc(row.label)}
              ${row.meta && Object.keys(row.meta).length > 0
                ? `<div class="meta">${esc(Object.entries(row.meta).map(([k, v]) => `${k}: ${v}`).join(' · '))}</div>`
                : ''}
            </td>
            ${(schema.row_fields ?? []).map(f => `<td>${f.type === 'checkbox' ? '&#9744;' : f.type === 'select' ? esc((f.options ?? []).join('/')) : '&nbsp;'}</td>`).join('')}
          </tr>`).join('')}
        </tbody>
      </table>`;

  // crossorigin="anonymous" -- without it, a cross-origin (Cloudinary) logo image taints the
  // html2canvas capture and canvas.toDataURL() throws in downloadTemplateBlankPdf's path.
  const generatedAt = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  return `<div class="sheet">
  <div class="header-band">
    ${logoUrl ? `<img class="header-logo" src="${esc(logoUrl)}" alt="" crossorigin="anonymous" />` : ''}
    <div class="header-text">
      ${facilityName && !logoUrl ? `<div class="header-facility">${esc(facilityName)}</div>` : ''}
      <h1 class="header-title">${esc(name)}</h1>
    </div>
  </div>
  <div class="meta-row">
    <div class="meta-item"><span>Date:</span><span class="meta-fill">&nbsp;</span></div>
    <div class="meta-item"><span>Completed by:</span><span class="meta-fill">&nbsp;</span></div>
  </div>
  ${schema.instructions ? `<p class="instructions">${esc(schema.instructions)}</p>` : ''}
  ${bodyContent}
  <div class="doc-footer">${facilityName ? `${esc(facilityName)} · ` : ''}Generated ${esc(generatedAt)}</div>
</div>`;
}

function buildTemplateBlankPrintHtml(name: string, schema: LogbookFormSchema, logoUrl?: string | null, facilityName?: string | null): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(name)}</title>
<style>${TEMPLATE_PRINT_CSS}</style>
</head>
<body>
${buildTemplateBlankBodyHtml(name, schema, logoUrl, facilityName)}
</body>
</html>`;
}

// Shared PDF generation, used by BOTH Print and Download -- previously Print (printTemplateBlank,
// removed) opened a second, separately-rendered HTML document in a new window and called the
// browser's native window.print() on it: a different rendering path from Download's jsPDF/
// html2canvas output, so a pagination fix applied to one (see downloadTemplateBlankPdf's blank-
// page fix) never touched the other, and print picked up the browser's own header/footer chrome
// (URL, title, "1 of 2", timestamp) since it was printing a live HTML document rather than a PDF.
// Renders the exact same standalone document (buildTemplateBlankPrintHtml -- not a second layout)
// inside an isolated off-screen iframe, the same technique the Preview panel below already uses
// via srcDoc, and rasterizes it with html2canvas into a real jsPDF document -- one generation path,
// consumed two ways below (pdf.save() for Download, blob-URL + iframe print for Print).
//
// LOCKED reasoning -- do not go back to injecting TEMPLATE_PRINT_CSS as a <style> tag into a div
// appended straight to the live document.body (the previous approach here): that leaked CSS in
// both directions since a <style> element applies document-wide regardless of DOM nesting.
// Outbound, TEMPLATE_PRINT_CSS's own bare `body {...}` rule overrode the real app's <body>
// background/color for the ~1-2s the capture container was attached, visible as the whole app
// appearing to switch to light theme. Inbound, global.css's bare `td { color: var(--text-primary) }`
// / `th { color: var(--text-muted) }` rules (global.css:266,274) leaked into the capture wherever
// TEMPLATE_PRINT_CSS didn't set its own `color` (it never did for th/td) -- in dark theme
// --text-primary is #e2e8f0 (near-white), rendered straight onto the print sheet's forced white
// background, producing unreadable near-white table text in the generated PDF. An iframe's
// document is fully isolated from the parent's CSSOM in both directions, so neither leak is
// possible; td/th correctly inherit TEMPLATE_PRINT_CSS's own body { color: #1a2233 } instead.
async function buildTemplateBlankPdf(name: string, schema: LogbookFormSchema, logoUrl?: string | null, facilityName?: string | null) {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed; left:-99999px; top:0; width:816px; height:1056px; border:0;';
  document.body.appendChild(iframe);

  try {
    await new Promise<void>((resolve) => {
      iframe.onload = () => resolve();
      iframe.srcdoc = buildTemplateBlankPrintHtml(name, schema, logoUrl, facilityName);
    });
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('Preview document unavailable');

    const images = Array.from(doc.querySelectorAll('img'));
    await Promise.all(images.map((img) => img.complete
      ? Promise.resolve()
      : new Promise<void>((resolve) => { img.onload = () => resolve(); img.onerror = () => resolve(); })));

    // Resize the iframe to its actual rendered content height so html2canvas captures the whole
    // sheet, not just whatever fit inside the default box above.
    iframe.style.height = `${doc.body.scrollHeight}px`;

    // useCORS: true -- without it, html2canvas silently omits the cross-origin facility logo
    // (loaded from the Railway API origin, cross-origin from the Vercel frontend) from the
    // captured canvas instead of throwing. The <img>'s own crossorigin="anonymous" attribute
    // (buildTemplateBlankBodyHtml above) only makes the browser load the image with CORS mode for
    // normal DOM rendering -- it's a separate, unrelated setting from html2canvas's own internal
    // image loader, which needs to be told explicitly to fetch cross-origin images at all. This is
    // exactly why the live preview iframe (plain DOM rendering, no html2canvas involved) always
    // showed the logo correctly while the downloaded/printed PDF (html2canvas capture) never did.
    const canvas = await html2canvas(doc.body, { backgroundColor: '#ffffff', scale: 2, useCORS: true });
    const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/png');

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    // Sub-point remainder here is float noise from the canvas-px -> pt conversion above, not real
    // content -- without this tolerance it triggers a whole extra blank page for a sliver only
    // fractions of a point tall.
    const ROUNDING_TOLERANCE_PT = 1;
    while (heightLeft > ROUNDING_TOLERANCE_PT) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    return pdf;
  } finally {
    document.body.removeChild(iframe);
  }
}

async function downloadTemplateBlankPdf(name: string, schema: LogbookFormSchema, logoUrl?: string | null, facilityName?: string | null) {
  const pdf = await buildTemplateBlankPdf(name, schema, logoUrl, facilityName);
  pdf.save(`${name}.pdf`);
}

// Print blank -- loads the exact same generated PDF (buildTemplateBlankPdf, shared with Download)
// into a hidden iframe via a blob URL and triggers the browser's native print dialog on it, instead
// of a second window.print() rendering path. Printing an actual PDF (rather than an HTML document)
// also sidesteps the browser's page-header/footer chrome (URL/title/"1 of N"/timestamp), since that
// only gets injected into HTML print jobs, not PDF-viewer print jobs.
async function printTemplateBlankPdf(name: string, schema: LogbookFormSchema, logoUrl?: string | null, facilityName?: string | null) {
  const pdf = await buildTemplateBlankPdf(name, schema, logoUrl, facilityName);
  const blobUrl = URL.createObjectURL(pdf.output('blob'));

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed; left:-99999px; top:0; width:0; height:0; border:0;';
  // src MUST be set before the iframe is inserted into the DOM -- appending first (as this used
  // to) makes the iframe initialize to about:blank and fire onload immediately (print dialog #1,
  // blank), then fire onload again once the blob src navigation completes (print dialog #2, the
  // real PDF). Setting src first means only the blob navigation ever happens, so onload fires once.
  iframe.onload = () => iframe.contentWindow?.print();
  iframe.src = blobUrl;
  document.body.appendChild(iframe);

  // Can't reliably detect "print dialog closed" cross-browser, so clean up on a delay long enough
  // for the user to have opened (and likely finished with) the print dialog -- same tradeoff the
  // removed printTemplateBlank made with its setTimeout before calling printWindow.print().
  setTimeout(() => {
    document.body.removeChild(iframe);
    URL.revokeObjectURL(blobUrl);
  }, 60000);
}

export default function LogbookTemplates() {
  const { user } = useAuth();
  const { facilityName } = useFacility();
  const logoUrl = useFacilityLogoUrl();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'org_admin';

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addCaretOpen, setAddCaretOpen] = useState(false);
  const [addLockDept, setAddLockDept] = useState(false);
  const [editing, setEditing] = useState<LogbookTemplate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<LogbookTemplate | null>(null);
  const [previewZoomed, setPreviewZoomed] = useState(false);
  const { toast, showToast } = useToast();
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [printingPdf, setPrintingPdf] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const { data: templatesRes, isLoading } = useQuery({
    queryKey: ['logbook-templates'],
    queryFn: () => api.get<LogbookTemplate[]>('/logbook-templates'),
  });
  const templates: LogbookTemplate[] = templatesRes?.data ?? [];

  const { data: deptsRes } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/departments'),
  });
  const departments: Department[] = deptsRes?.data ?? [];

  // Default-select the first template once data loads; if the selected row disappears
  // (deleted, or filtered out of view server-side after a role/department change), fall back.
  useEffect(() => {
    if (selectedId !== null && templates.some((t) => t.id === selectedId)) return;
    setSelectedId(templates[0]?.id ?? null);
  }, [templates, selectedId]);

  const filtered = useMemo(
    () => templates.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [templates, search]
  );

  // Group by department, preserving server order (facility-wide first, then departments by
  // sort_order) — first-seen-order iteration, not a client-side re-sort.
  const groups = useMemo(() => {
    const list: { key: string; label: string; color: string; icon: string | null; items: LogbookTemplate[] }[] = [];
    const idx = new Map<string, number>();
    filtered.forEach((t) => {
      const key = t.department_id === null ? 'facility-wide' : String(t.department_id);
      if (!idx.has(key)) {
        idx.set(key, list.length);
        list.push({
          key,
          label: t.department_id === null ? 'Facility-Wide' : (t.department_name ?? 'Department'),
          color: t.department_id === null ? '#ef4444' : (t.department_color ?? '#6b7280'),
          icon: t.department_id === null ? null : t.department_icon,
          items: [],
        });
      }
      list[idx.get(key)!].items.push(t);
    });
    return list;
  }, [filtered]);

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  // Disaster Manual moved to its own system in Phase 1.5 (Reports > Logbooks > Disaster Manual —
  // disaster_manual_sections/disaster-manual.ts), a deliberate separation per Chris, not an
  // extension of this template library. This button used to find-or-create a row in
  // logbook_templates named "Emergency & Disaster Preparedness Manual" (a Phase 1 stub written
  // before Phase 1.5 existed) — fixed here to navigate to the real system instead of creating a
  // confusing duplicate that has nothing to do with the actual Disaster Manual.
  function handleDisasterManualClick() {
    navigate('/reports?tab=logbooks');
  }

  function openAddNormal() {
    setAddLockDept(false);
    setAddOpen(true);
    setAddCaretOpen(false);
  }
  function openAddFacilityWide() {
    setAddLockDept(true);
    setAddOpen(true);
    setAddCaretOpen(false);
  }

  async function handleReplace(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.postForm(`/logbook-templates/${selected.id}/replace`, fd);
      qc.invalidateQueries({ queryKey: ['logbook-templates'] });
      showToast('File replaced');
    } catch {
      showToast('Replace failed');
    }
    e.target.value = '';
  }

  async function handleDelete() {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/logbook-templates/${deleteConfirm.id}`);
      qc.invalidateQueries({ queryKey: ['logbook-templates'] });
      showToast('Template deleted');
    } catch {
      showToast('Delete failed');
    }
    setDeleteConfirm(null);
  }

  return (
    <div>
      <div className="floating-action-cluster"><PageFocusToggle /></div>

      <Toast toast={toast} />

      {/* Top bar — title/subtitle + action buttons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', padding: '16px 20px 0' }}>
        <div>
          <h2 style={{ fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.01em', margin: 0 }}>Logbook Templates</h2>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 2 }}>
            Routine log templates &amp; reference manuals · ordered to match your printed binder{facilityName ? ` · ${facilityName}` : ''}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" title="Coming soon" disabled style={{ opacity: 0.5, cursor: 'default' }}>
            Filled logs
          </button>
          <button className="btn btn-ghost" onClick={() => window.print()}>
            <Printer size={14} /> Print binder
          </button>
          {/* Distinctly-styled Disaster Manual button — reuses mockup's .disaster red/amber gradient */}
          <button
            onClick={handleDisasterManualClick}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 15px',
              borderRadius: 6, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
              border: '1px solid rgba(239,68,68,0.4)',
              background: 'linear-gradient(120deg, rgba(239,68,68,0.16), rgba(245,158,11,0.08) 60%, transparent)',
              color: '#fca5a5',
            }}
          >
            <Siren size={15} /> Disaster Manual
          </button>
          {isAdmin && (
            <span style={{ display: 'inline-flex', borderRadius: 6, position: 'relative' }}>
              <button className="btn btn-primary combo2" style={{ borderRadius: '6px 0 0 6px' }} onClick={openAddNormal}>
                <Plus size={14} /> Add log
              </button>
              <button
                className="btn btn-primary combo2"
                style={{ borderRadius: '0 6px 6px 0', borderLeft: '1px solid rgba(255,255,255,0.25)', padding: '9px 10px' }}
                onClick={() => setAddCaretOpen((o) => !o)}
              >
                <ChevronDown size={14} />
              </button>
              {addCaretOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 400, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', minWidth: 200, padding: 4 }}>
                  <button
                    onClick={openAddFacilityWide}
                    style={{ display: 'flex', width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8125rem', textAlign: 'left' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    Add facility-wide item
                  </button>
                </div>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Two-pane workspace — View E ("Workspace") from mockups/logbook-templates-page-enhanced.html */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 340px) 1fr', gap: 16, alignItems: 'start', padding: '16px 20px 24px' }}>
        {/* Left pane — searchable, department-grouped list */}
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
            <div className="search-bar" style={{ width: '100%' }}>
              <Search size={15} color="var(--text-muted)" />
              <input type="text" placeholder="Find a template…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="scrollable" style={{ maxHeight: 620, overflowY: 'auto', padding: 8 }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 12 }}>Loading…</div>
            ) : groups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
                {search ? 'No templates match your search.' : 'No templates yet.'}
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.key}>
                  <div style={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', padding: '10px 8px 4px', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: g.color, flexShrink: 0 }} />
                    {g.key === 'facility-wide' ? <AlertTriangle size={11} color={g.color} /> : <DeptIcon icon={g.icon} size={11} color={g.color} />}
                    {g.label}
                  </div>
                  {g.items.map((t, i) => {
                    const isSelected = t.id === selectedId;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setSelectedId(t.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 9,
                          cursor: 'pointer', marginBottom: 2, transition: 'background 0.12s',
                          background: isSelected ? 'rgba(59,130,246,0.08)' : 'transparent',
                          borderLeft: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                          paddingLeft: isSelected ? 8 : 10,
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: 'var(--text-muted)' }}>{i + 1}</span>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: `${g.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {fileIcon(t.file_type, 14)}
                        </div>
                        <span style={{ flex: 1, minWidth: 0, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                        {/* Badges only — no per-row action buttons in Phase 1, so there's nothing
                            clickable here for the floating focus-toggle to ever cover. If action
                            icons are added to this row later, they must come before these badges
                            in flex order (same fix as Tasks.tsx Reminders rows). */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                          {t.required && <Star size={11} color="#fbbf24" fill="#fbbf24" />}
                          {schemaHasFillableFields(t.form_schema) && <ClipboardList size={11} color="#34d399" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right pane — detail view for the selected template */}
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 14, background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
          {!selected ? (
            <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              {isLoading ? 'Loading…' : 'Select a template to view details.'}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '20px 22px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ width: 50, height: 50, borderRadius: 13, background: selected.department_id === null ? 'rgba(239,68,68,0.18)' : `${selected.department_color ?? '#6b7280'}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {selected.department_id === null ? <Siren size={24} color="#f87171" /> : fileIcon(selected.file_type, 22)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{selected.name}</div>
                  <div style={{ display: 'flex', gap: 7, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FreqBadge freq={selected.frequency} />
                    <FileTypeBadge type={selected.file_type} />
                    {selected.required && <RequiredBadge />}
                    {schemaHasFillableFields(selected.form_schema) && <FormBadge />}
                    <SourceBadge source={selected.source} />
                    <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {selected.department_id === null
                        ? 'Facility-wide'
                        : <><DeptIcon icon={selected.department_icon} size={11} color={selected.department_color ?? undefined} /> {selected.department_name} · binder #{selected.display_order}</>}
                    </span>
                  </div>
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => setEditing(selected)} title="Edit" style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => setDeleteConfirm(selected)} title="Delete" style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 9, padding: '16px 22px', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
                {(() => {
                  const hasFile = !!selected.file_url;
                  const hasFields = schemaHasFillableFields(selected.form_schema);
                  const canExport = hasFile || hasFields;
                  // Schema-only templates (no uploaded reference file) have nothing to literally
                  // download as a file — Download generates a real .pdf client-side from the same
                  // print markup (same fix pattern as Print blank: previously both buttons were
                  // hard-gated on file_url alone, leaving form-only templates with no export path
                  // of any kind).
                  const handlePrint = async () => {
                    if (hasFile) { window.open(selected.file_url!, '_blank'); return; }
                    if (!hasFields || printingPdf) return;
                    setPrintingPdf(true);
                    try {
                      await printTemplateBlankPdf(selected.name, selected.form_schema!, logoUrl, facilityName);
                    } catch {
                      showToast('PDF generation failed');
                    }
                    setPrintingPdf(false);
                  };
                  const handleDownload = async () => {
                    if (!hasFields || downloadingPdf) return;
                    setDownloadingPdf(true);
                    try {
                      await downloadTemplateBlankPdf(selected.name, selected.form_schema!, logoUrl, facilityName);
                    } catch {
                      showToast('PDF generation failed');
                    }
                    setDownloadingPdf(false);
                  };
                  return (
                    <>
                      <button
                        className="btn btn-primary combo2"
                        disabled={!canExport || printingPdf}
                        title={canExport ? undefined : 'No file uploaded and no fillable fields'}
                        style={!canExport ? { opacity: 0.5, cursor: 'default' } : undefined}
                        onClick={handlePrint}
                      >
                        <Printer size={14} /> {printingPdf ? 'Preparing…' : 'Print blank'}
                      </button>
                      {hasFile ? (
                        <a
                          className="btn btn-ghost"
                          href={selected.file_url ?? undefined}
                          target="_blank" rel="noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          <Download size={14} /> Download
                        </a>
                      ) : (
                        <button
                          className="btn btn-ghost"
                          disabled={!canExport || downloadingPdf}
                          title={canExport ? 'No file uploaded — generates a PDF from the blank form' : 'No file uploaded and no fillable fields'}
                          style={!canExport ? { opacity: 0.5, cursor: 'default' } : undefined}
                          onClick={handleDownload}
                        >
                          <Download size={14} /> {downloadingPdf ? 'Generating…' : 'Download'}
                        </button>
                      )}
                    </>
                  );
                })()}
                {isAdmin && (
                  <>
                    <button className="btn btn-ghost" onClick={() => replaceInputRef.current?.click()}>
                      <Upload size={14} /> Replace
                    </button>
                    <input ref={replaceInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleReplace} />
                  </>
                )}
              </div>

              <div style={{ padding: '20px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                <div>
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 15, background: 'rgba(255,255,255,0.02)', marginBottom: 14 }}>
                    <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>Preview</div>
                    {selected.file_url ? (
                      <div style={{
                        aspectRatio: '8.5 / 11', maxHeight: 200, borderRadius: 8,
                        background: 'repeating-linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.04) 14px, transparent 14px, transparent 28px)',
                        border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text-muted)', fontSize: '0.7rem', textAlign: 'center', padding: 12,
                      }}>
                        {`${selected.file_type.toUpperCase()} template preview`}
                      </div>
                    ) : schemaHasFillableFields(selected.form_schema) ? (
                      // Print-style preview, not the interactive fill-mode renderer -- reuses the
                      // exact HTML "Print blank" generates (buildTemplateBlankPrintHtml) via an
                      // iframe, so this shows what the printed page will actually look like
                      // instead of a second, divergent rendering path. Thumbnail-sized by default
                      // (maxHeight:200) -- clicking it opens the same iframe full-size in a modal,
                      // reusing this file's own existing modal-overlay/portal pattern (see the
                      // delete-confirm modal below) rather than a new lightbox component.
                      <div
                        onClick={() => setPreviewZoomed(true)}
                        title="Click to enlarge"
                        style={{ cursor: 'zoom-in' }}
                      >
                        <iframe
                          title="Template preview"
                          srcDoc={buildTemplateBlankPrintHtml(selected.name, selected.form_schema!, logoUrl, facilityName)}
                          style={{
                            width: '100%', aspectRatio: '8.5 / 11', maxHeight: 200, borderRadius: 8,
                            border: '1px solid var(--border-default)', background: '#fff', pointerEvents: 'none',
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{
                        aspectRatio: '8.5 / 11', maxHeight: 200, borderRadius: 8,
                        background: 'repeating-linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.04) 14px, transparent 14px, transparent 28px)',
                        border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text-muted)', fontSize: '0.7rem', textAlign: 'center', padding: 12,
                      }}>
                        No file uploaded yet
                      </div>
                    )}
                  </div>
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 15, background: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <ClipboardList size={12} /> Form fields
                    </div>
                    {schemaHasFillableFields(selected.form_schema) ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {selected.form_schema!.form_mode === 'grid' && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                            Grid — {selected.form_schema!.rows?.length ?? 0} rows × {selected.form_schema!.row_fields?.length ?? 0} columns
                          </div>
                        )}
                        {(selected.form_schema!.form_mode === 'single_form' ? selected.form_schema!.fixed_fields : selected.form_schema!.row_fields)?.map((f) => (
                          <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{f.label}</span>
                            <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>{f.type}</span>
                            {f.required && <Star size={9} color="#fbbf24" fill="#fbbf24" />}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>
                        No form attached. This is a blank/reference template only — Work Orders can't attach a fillable form for it.
                      </div>
                    )}
                  </div>
                </div>
                <RecentFilledLogsPanel templateId={selected.id} navigate={navigate} />
              </div>
            </>
          )}
        </div>
      </div>

      {(addOpen || editing) && (
        <TemplateFormModal
          mode={editing ? 'edit' : 'add'}
          initial={editing ?? undefined}
          lockDepartment={addLockDept}
          departments={departments}
          onClose={() => { setAddOpen(false); setEditing(null); setAddLockDept(false); }}
          onSaved={(msg) => { qc.invalidateQueries({ queryKey: ['logbook-templates'] }); showToast(msg); setAddOpen(false); setEditing(null); setAddLockDept(false); }}
        />
      )}

      {deleteConfirm && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 380, width: '90vw' }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Template?</h3>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}><X size={18} /></button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{deleteConfirm.name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn" style={{ background: '#ef4444', color: '#fff' }} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {previewZoomed && selected && schemaHasFillableFields(selected.form_schema) && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setPreviewZoomed(false)}>
          <div className="modal" style={{ maxWidth: 640, width: '92vw', maxHeight: '92vh', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '14px 18px', flexShrink: 0 }}>
              <h3 className="modal-title">{selected.name} — Preview</h3>
              <button className="modal-close" onClick={() => setPreviewZoomed(false)}><X size={18} /></button>
            </div>
            <iframe
              title="Template preview (zoomed)"
              srcDoc={buildTemplateBlankPrintHtml(selected.name, selected.form_schema!, logoUrl, facilityName)}
              style={{ width: '100%', aspectRatio: '8.5 / 11', border: 'none', background: '#fff', flex: '0 1 auto', minHeight: 0 }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Recent filled logs — real data now that logbook_filled_entries exists (Phase 3) ───────

interface FilledEntrySummary {
  id: number;
  filled_at: string;
  filled_by_first_name: string | null;
  filled_by_last_name: string | null;
  filled_by_username: string | null;
}

function RecentFilledLogsPanel({ templateId, navigate }: { templateId: number; navigate: (path: string) => void }) {
  const { data } = useQuery({
    queryKey: ['logbook-filled-entries', 'by-template', templateId],
    queryFn: () => api.get<FilledEntrySummary[]>('/logbook-filled-entries', { template_id: templateId }),
  });
  const entries = data?.data ?? [];

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 15, background: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
        Recent filled logs · {entries.length} saved
      </div>
      {entries.length === 0 ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>No filled logs yet for this template.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.slice(0, 3).map((e) => {
            const name = e.filled_by_first_name || e.filled_by_last_name
              ? `${e.filled_by_first_name ?? ''} ${e.filled_by_last_name ?? ''}`.trim()
              : (e.filled_by_username ?? 'Unknown');
            return (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.78rem' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#93c5fd', fontSize: '0.72rem', flexShrink: 0 }}>
                  {new Date(e.filled_at).toLocaleDateString()}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>{name}</span>
              </div>
            );
          })}
        </div>
      )}
      <button
        onClick={() => navigate('/reports?tab=logbooks')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: '0.74rem', fontWeight: 600, color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        View all filled logs in Reports →
      </button>
    </div>
  );
}

// ─── Add/Edit modal ─────────────────────────────────────────────────────────

function TemplateFormModal({ mode, initial, lockDepartment, departments, onClose, onSaved }: {
  mode: 'add' | 'edit';
  initial?: LogbookTemplate;
  lockDepartment: boolean;
  departments: Department[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [departmentId, setDepartmentId] = useState<string>(
    lockDepartment ? '' : initial?.department_id != null ? String(initial.department_id) : ''
  );
  const [frequency, setFrequency] = useState<Frequency>(initial?.frequency ?? 'as_needed');
  const [source, setSource] = useState<Source>(initial?.source ?? 'building');
  const [required, setRequired] = useState(initial?.required ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  // This visual builder only authors single_form schemas — grid templates (JSON injector only)
  // aren't representable here, so opening Edit on one just shows no fields rather than crashing;
  // handleSubmit below preserves the grid schema untouched rather than deriving from formFields.
  const isGridTemplate = initial?.form_schema?.form_mode === 'grid';
  // Reconstruct section_break markers from the saved sections/section_id shape so the builder
  // shows them for editing — see FormSchemaBuilder.tsx's mergeSectionsIntoFields/
  // splitFieldsIntoSections for the two-way conversion.
  const [formFields, setFormFields] = useState<FormField[]>(
    () => mergeSectionsIntoFields(initial?.form_schema?.fixed_fields ?? [], initial?.form_schema?.sections ?? [])
  );
  const fileRef = useRef<HTMLInputElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const deptOptions = [{ value: '', label: 'Facility-wide (no department)' }, ...departments.map((d) => ({ value: String(d.id), label: d.name }))];

  function deriveFileType(f: File): FileKind {
    return /\.docx?$/i.test(f.name) ? 'docx' : 'pdf';
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    // Blank-label real fields are dropped same as before; section_break markers keep their own
    // fallback label ("Section") in splitFieldsIntoSections rather than being filtered here.
    const cleanFields = formFields.filter((f) => f.type === 'section_break' || f.label.trim());
    const { fields: sectionedFields, sections: builtSections } = splitFieldsIntoSections(cleanFields);
    // Grid templates are JSON-injector-only — never destructively overwrite one from this
    // visual (single_form-only) builder just because its own formFields state happens to be
    // empty (it always is, for a grid template, since this UI can't represent grid schemas).
    const schemaPayload: LogbookFormSchema | null = isGridTemplate
      ? initial!.form_schema
      : (sectionedFields.length > 0
          ? { form_mode: 'single_form', fixed_fields: sectionedFields, ...(builtSections.length > 0 ? { sections: builtSections } : {}) }
          : null);
    try {
      if (mode === 'add') {
        const fd = new FormData();
        fd.append('name', name.trim());
        if (departmentId) fd.append('department_id', departmentId);
        fd.append('frequency', frequency);
        fd.append('file_type', file ? deriveFileType(file) : 'pdf');
        fd.append('source', source);
        fd.append('required', String(required));
        if (schemaPayload) fd.append('form_schema', JSON.stringify(schemaPayload));
        if (file) fd.append('file', file);
        await api.postForm('/logbook-templates', fd);
        onSaved('Template created');
      } else if (initial) {
        await api.put(`/logbook-templates/${initial.id}`, {
          name: name.trim(),
          department_id: departmentId || null,
          frequency,
          file_type: initial.file_type,
          source,
          required,
          display_order: initial.display_order,
          form_schema: schemaPayload,
        });
        onSaved('Template updated');
      }
    } catch {
      onSaved('Save failed');
    }
    setSaving(false);
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay"
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 620, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Fixed Header */}
        <div style={{ flexShrink: 0, padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.0625rem', fontWeight: 700 }}>
            <FileText size={18} className="modal-icon-intro" style={{ color: '#3b82f6' }} />
            {mode === 'add' ? 'Add Log Template' : 'Edit Log Template'}
          </h3>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Name</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Department</label>
            <DarkSelect
              value={departmentId}
              onChange={setDepartmentId}
              options={deptOptions}
              placeholder="Facility-wide (no department)"
            />
            {lockDepartment && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Facility-wide item — leave department unset to keep it visible to everyone.</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Frequency</label>
              <DarkSelect
                value={frequency}
                onChange={(v) => setFrequency(v as Frequency)}
                options={Object.entries(FREQ_LABELS).map(([value, label]) => ({ value, label }))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Source</label>
              <DarkSelect
                value={source}
                onChange={(v) => setSource(v as Source)}
                options={[{ value: 'building', label: 'Building' }, { value: 'corporate', label: 'Corporate' }]}
              />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
            Required
          </label>
          {mode === 'add' && (
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>File (optional — can upload later via Replace)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, background: 'var(--bg-elevated)' }}>
                <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: file ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {file ? file.name : 'No file selected'}
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>Choose file</button>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </div>
            </div>
          )}

          {/* Form builder — Phase 2. null form_schema (no fields added) = blank template only,
              no fillable form; a Work Order can't attach one for it. Shared FormSchemaBuilder
              (also used by Tasks.tsx for a WO's own ad-hoc checklist/fields). Single_form only —
              grid templates are authored via the JSON injector (Settings > Development). */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
            {isGridTemplate ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                This template uses a grid layout (rows of locations/assets) — edit its fields via Settings &gt; Development, not here. Name/department/frequency above can still be edited normally.
              </div>
            ) : (
              <FormSchemaBuilder
                fields={formFields}
                onChange={setFormFields}
                stableKeys={initial?.form_schema?.fixed_fields?.map(f => f.key) ?? []}
                label="Fillable form fields (optional)"
                addLabel="Add field"
                showRangeLimits
                allowSections
                emptyLabel="No fields — this stays a blank/reference-only template. Work Orders won't be able to attach a form for it."
              />
            )}
          </div>
        </div>
        {/* Fixed Footer */}
        <div style={{ flexShrink: 0, padding: '16px 24px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary combo2" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
